import { useState, useEffect, useRef } from 'react';
import { useRTCClient, useLocalMicrophoneTrack, useLocalCameraTrack, LocalUser } from 'agora-rtc-react';
import { liveStreamAPI } from '../../api/liveStreamApis/liveStream.api';
import { LiveStreamChat } from './LiveStreamChat';
import { ImagePlus, X, Mic, MicOff, Video, VideoOff, Users, Heart, Wifi } from 'lucide-react'; 
import { toast } from 'sonner';

// 🟢 IMPORTED THE CUSTOM HOOK
import { useLiveStreamStatus } from '../../hooks/liveStreamHooks/useLiveStreamStatus';

export const ExpertLiveStream = () => {
  const client = useRTCClient(); 
  
  const savedChannel = sessionStorage.getItem('live_channelName');
  const [isLive, setIsLive] = useState(!!savedChannel);
  const [channelName, setChannelName] = useState(savedChannel || '');
  const [streamTitle, setStreamTitle] = useState(sessionStorage.getItem('live_title') || '');
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false); 
  
  const [isStarting, setIsStarting] = useState(false);
  const [hasPermission, setHasPermission] = useState(true);

  const [viewers, setViewers] = useState(0);
  const [likes, setLikes] = useState(0);

  const expertCode = localStorage.getItem('expertCode') || '';
  const expertName = localStorage.getItem('expertName') || 'Expert';
  
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const isEndingRef = useRef(false);
  const triggerStreamEndRef = useRef<(() => void) | null>(null);

  const { localMicrophoneTrack } = useLocalMicrophoneTrack(isLive);
  const { localCameraTrack } = useLocalCameraTrack(isLive);

  useEffect(() => {
    const manageConnection = async () => {
      const token = sessionStorage.getItem('live_token');
      const appId = sessionStorage.getItem('live_appId');

      if (isLive && token && appId && channelName && localMicrophoneTrack && localCameraTrack) {
        try {
          if (!client.channelName) {
            await client.setClientRole("host");
            await client.join(appId, channelName, token, null);
          }
          
          const hasPublishedAudio = client.localTracks.some(t => t.trackMediaType === 'audio');
          const hasPublishedVideo = client.localTracks.some(t => t.trackMediaType === 'video');
          
          const tracksToPublish = [];
          if (!hasPublishedAudio) tracksToPublish.push(localMicrophoneTrack);
          if (!hasPublishedVideo) tracksToPublish.push(localCameraTrack);

          if (tracksToPublish.length > 0) {
            await client.publish(tracksToPublish);
          }
        } catch (error) {
          console.error("Agora Connection/Publish Error:", error);
          if (!client.channelName) {
            sessionStorage.removeItem('live_channelName');
            sessionStorage.removeItem('live_token');
            sessionStorage.removeItem('live_appId');
            setIsLive(false);
          }
        }
      }
    };
    manageConnection();
  }, [isLive, localMicrophoneTrack, localCameraTrack, client, channelName]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setThumbnailFile(file);
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };

  const removeThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailPreview(null);
  };

  const handleToggleMute = async () => {
    if (localMicrophoneTrack) {
      await localMicrophoneTrack.setEnabled(isMuted);
      setIsMuted(!isMuted);
    }
  };

  const handleToggleVideo = async () => {
    if (localCameraTrack) {
      await localCameraTrack.setEnabled(isVideoOff);
      setIsVideoOff(!isVideoOff);
    }
  };

  const handleStart = async () => {
    if (!expertCode) return;
    const finalTitle = streamTitle.trim();
    if (!finalTitle) return;

    setIsStarting(true);

    try {
      const response = await liveStreamAPI.startStream({ 
        title: finalTitle,
        thumbnail: thumbnailFile
      });
      
      setChannelName(response.channelName);
      sessionStorage.setItem('live_channelName', response.channelName);
      sessionStorage.setItem('live_token', response.token);
      sessionStorage.setItem('live_appId', response.appId);
      sessionStorage.setItem('live_title', finalTitle);
      
      await client.setClientRole("host");
      await client.join(response.appId, response.channelName, response.token, null);
      
      setIsLive(true);
    } catch (error: any) {
      console.error("Failed to start stream:", error);
      if (error.response?.status === 403) {
        toast.error("You don't have permission for live session. Contact admin.");
        setHasPermission(false);
      } else {
        toast.error("Failed to start live stream. Please try again.");
      }
    } finally {
      setIsStarting(false);
    }
  };

 const handleEnd = async () => {
    if (isEndingRef.current) return;
    isEndingRef.current = true;

    try {
      await liveStreamAPI.endStream({ channelName });
      
      // 🟢 Emit socket event to notify all users stream ended
      if (triggerStreamEndRef.current) {
        triggerStreamEndRef.current();
      }
    } catch (error) {
      // 🟢 Even if backend says 400 (already ended), still clean up frontend
      console.warn('End stream API failed, cleaning up anyway:', error);
      
      // 🟢 Still try to emit socket event so users get redirected
      if (triggerStreamEndRef.current) {
        triggerStreamEndRef.current();
      }
    } finally {
      // 🟢 ALWAYS runs — cleans up tracks, Agora, state, sessionStorage
      try {
        if (localCameraTrack) {
          localCameraTrack.stop();
          localCameraTrack.close();
        }
        if (localMicrophoneTrack) {
          localMicrophoneTrack.stop();
          localMicrophoneTrack.close();
        }
        await client.unpublish();
        await client.leave();
      } catch (cleanupError) {
        console.warn('Agora cleanup error:', cleanupError);
      }

      sessionStorage.removeItem('live_channelName');
      sessionStorage.removeItem('live_token');
      sessionStorage.removeItem('live_appId');
      sessionStorage.removeItem('live_title');

      setIsLive(false);
      setChannelName('');
      setStreamTitle('');
      setIsVideoOff(false); 
      setIsMuted(false); 
      isEndingRef.current = false;
    }
  };

  useEffect(() => {
    return () => {
      if (isLive && !sessionStorage.getItem('live_channelName')) {
        localCameraTrack?.close();
        localMicrophoneTrack?.close();
        client.leave().catch(console.error);
      }
    };
  }, [isLive, client, localCameraTrack, localMicrophoneTrack]);

  // 🟢 NEW LOGIC: CHECK IF EXPERT IS ALREADY LIVE ON ANOTHER DEVICE
  const liveExperts = useLiveStreamStatus();
  const isAlreadyLiveOnAnotherDevice = liveExperts[expertCode] && !sessionStorage.getItem('live_channelName');

  if (isAlreadyLiveOnAnotherDevice) {
    return (
      <div className="w-full max-w-6xl mx-auto p-4 font-['Poppins']">
        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 h-[700px] flex flex-col items-center justify-center p-8 text-center">
          <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
            <Wifi className="w-10 h-10 text-red-500 animate-pulse" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-3">Already Live on Another Device</h2>
          <p className="text-slate-500 text-center max-w-md leading-relaxed">
            You are currently live streaming from another device or browser tab. Please end that session first before starting a new one.
          </p>
        </div>
      </div>
    );
  }

  // 🟢 MAIN RENDER (UNCHANGED)
  return (
    <div className="w-full max-w-6xl mx-auto p-4 font-['Poppins']">
      <div className="bg-white rounded-xl overflow-hidden shadow-2xl border border-gray-200 relative flex flex-col md:flex-row h-[700px]">
        
        {/* Left Side */}
        <div className="flex-1 relative flex flex-col bg-slate-50">
          
          <div className="absolute top-0 left-0 w-full p-4 z-10 flex justify-between items-center bg-blue-700">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${isLive ? 'bg-red-500 animate-pulse' : 'bg-slate-400'}`}></div>
              <span className="text-white font-medium text-sm drop-shadow-md">{isLive ? 'LIVE NOW' : 'You are offline now'}</span>
            </div>
            
            {channelName && (
              <div className="flex items-center gap-3">
                {isLive && (
                  <div className="flex items-center gap-3 text-white/90 text-sm font-semibold bg-black/20 px-3 py-1 rounded shadow-sm">
                    <span className="flex items-center gap-1.5" title="Viewers"><Users className="w-4 h-4" /> {viewers}</span>
                    <span className="w-px h-3 bg-white/30"></span>
                    <span className="flex items-center gap-1.5" title="Likes"><Heart className="w-4 h-4 text-pink-400 fill-pink-400/30" /> {likes}</span>
                  </div>
                )}
                <span className="text-white/90 text-xs font-mono bg-black/30 backdrop-blur-sm px-2.5 py-1 rounded shadow-sm">RoomID: {channelName}</span>
              </div>
            )}
          </div>

          <div className="w-full h-full flex items-center justify-center relative">
            {!isLive ? (
              <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-sm border border-gray-100 z-10 mx-4">
                
                {/* 🟢 Render Permission Denied message OR the Form */}
                {!hasPermission ? (
                  <div className="text-center py-6">
                    <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                      <X className="w-8 h-8 text-red-500" />
                    </div>
                    <h2 className="text-xl font-bold text-slate-800">Access Denied</h2>
                    <p className="text-slate-500 text-sm mt-2">
                      You don't have permission for live sessions. Please contact admin or support.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="text-center mb-8">
                      <h2 className="text-2xl font-bold text-slate-800">Start Your Live Session</h2>
                      <p className="text-slate-500 text-sm mt-1">Ready {expertName}?</p>
                    </div>
                    
                    <div className="mb-5 text-left">
                      <label className="block text-slate-700 text-sm font-semibold mb-2">Stream Title <span className="text-red-500">*</span></label>
                      <input 
                        type="text" 
                        value={streamTitle}
                        onChange={(e) => setStreamTitle(e.target.value)}
                        className="w-full bg-slate-50 text-slate-900 px-4 py-3 rounded-lg border border-slate-200 focus:bg-white focus:border-[#083c61] focus:ring-2 focus:ring-[#083c61]/20 outline-none transition-all placeholder:text-slate-400"
                        placeholder="e.g., Today's Market Analysis & Q&A"
                        disabled={isStarting} 
                      />
                    </div>

                    <div className="mb-8 text-left">
                      <label className="block text-slate-700 text-sm font-semibold mb-2">Stream Thumbnail <span className="text-slate-400 font-normal">(Optional)</span></label>
                      
                      {!thumbnailPreview ? (
                        <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-slate-200 border-dashed rounded-lg bg-slate-50 transition-colors group ${isStarting ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-slate-100'}`}>
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <ImagePlus className="w-8 h-8 mb-2 text-slate-400 group-hover:text-[#083c61] transition-colors" />
                            <p className="text-sm text-slate-500"><span className="font-semibold text-[#083c61]">Click to upload</span> an image</p>
                          </div>
                          <input type="file" className="hidden" accept="image/*" onChange={handleImageChange} disabled={isStarting} />
                        </label>
                      ) : (
                        <div className={`relative w-full h-32 rounded-lg overflow-hidden border border-slate-200 shadow-sm group ${isStarting ? 'opacity-70' : ''}`}>
                          <img src={thumbnailPreview} alt="Preview" className="w-full h-full object-cover" />
                          {!isStarting && (
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <button onClick={removeThumbnail} className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg">
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <button 
                      onClick={handleStart}
                      disabled={!streamTitle.trim() || isStarting}
                      className={`w-full py-3.5 text-white font-bold rounded-xl shadow-lg transition-all flex justify-center items-center gap-2 ${
                        streamTitle.trim() && !isStarting
                          ? 'bg-[#083c61] hover:bg-[#062a44] hover:shadow-xl cursor-pointer' 
                          : 'bg-slate-400 cursor-not-allowed opacity-70'
                      }`}
                    >
                      {isStarting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Starting...
                        </>
                      ) : (
                        <>
                          <div className={`w-2 h-2 rounded-full ${streamTitle.trim() ? 'bg-red-500 animate-pulse' : 'bg-slate-300'}`}></div>
                          Start Live Stream
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="w-full h-full bg-black">
                <LocalUser 
                  audioTrack={localMicrophoneTrack} 
                  videoTrack={localCameraTrack} 
                  cameraOn={!isVideoOff} 
                  micOn={!isMuted}
                  playAudio={false} 
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>

          {isLive && (
            <div className="absolute bottom-0 left-0 w-full p-6 z-10 bg-gradient-to-t from-black/90 to-transparent flex justify-center items-center gap-4">
              
              <button 
                onClick={handleToggleMute}
                className={`p-3 rounded-full border transition-colors shadow-lg ${
                  isMuted 
                    ? 'bg-red-500 border-red-400 text-white hover:bg-red-600' 
                    : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                }`}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>

              <button 
                onClick={handleToggleVideo}
                className={`p-3 rounded-full border transition-colors shadow-lg ${
                  isVideoOff 
                    ? 'bg-red-500 border-red-400 text-white hover:bg-red-600' 
                    : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                }`}
                title={isVideoOff ? 'Turn Camera On' : 'Turn Camera Off'}
              >
                {isVideoOff ? <VideoOff className="w-5 h-5" /> : <Video className="w-5 h-5" />}
              </button>

              <button 
                onClick={handleEnd}
                className="px-8 py-2.5 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-full shadow-lg transition-colors border border-red-400 flex items-center gap-2"
              >
                <span className="w-2 h-2 bg-white rounded-full"></span>
                End Stream
              </button>
            </div>
          )}
        </div>

        {isLive && (
          <div className="w-full md:w-80 h-64 md:h-full flex-shrink-0 border-t md:border-t-0 md:border-l border-gray-200 bg-white">
            <LiveStreamChat 
              isExpertMode={true} 
              expertName={expertCode} 
              userName={expertName}
              channelName={channelName}
              onRegisterEndTrigger={(fn) => { triggerStreamEndRef.current = fn; }}
              onViewersUpdate={setViewers}
              onLikesUpdate={setLikes}
            />
          </div>
        )}
      </div>
    </div>
  );
};